import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Filter, Sparkles, Lock, Info, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * FilterAttributesCard — Dynamic product attributes for Shopify metafield filters.
 * 
 * Renders inside UniversalSinglePageForm after category/subcategory selection.
 * Fetches inherited filter sets based on category path, shows attribute inputs,
 * and supports keyword auto-fill from product title.
 * 
 * Props:
 *   categoryId          - Selected category ID (string). Empty when the
 *                          listing's stored category NAME can't be
 *                          resolved to an ID (see categoryNameHint).
 *   subcategoryId       - Selected subcategory ID (optional)
 *   productTitle        - Current product title (for keyword matching)
 *   values              - Current filter_attributes values { "custom.key": "value" }
 *   onChange            - Callback: (newValues) => void
 *   embedded            - Render without a Card wrapper (default false)
 *   categoryNameHint    - iter-294 Bug-2 fix — the RAW category NAME
 *                          stored on the listing. When we have a name
 *                          but couldn't resolve it to an ID (taxonomy
 *                          drift), we render a diagnostic empty state
 *                          instead of returning null. Prevents the
 *                          "Filter section completely missing"
 *                          symptom Hare Krishna Enterprises hit.
 */
const FilterAttributesCard = ({ categoryId, subcategoryId, productTitle, values = {}, onChange, embedded = false, categoryNameHint = '' }) => {
  const { apiCall } = useAuth();
  const [attributes, setAttributes] = useState([]);
  const [filterSets, setFilterSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({});
  const prevCatRef = useRef('');

  // Fetch resolved attributes when category changes
  const fetchAttributes = useCallback(async () => {
    if (!categoryId) {
      setAttributes([]);
      setFilterSets([]);
      return;
    }

    const cacheKey = `${categoryId}|${subcategoryId || ''}`;
    if (cacheKey === prevCatRef.current) return;
    prevCatRef.current = cacheKey;

    setLoading(true);
    try {
      let url = `/api/filter-sets/resolve?category_id=${categoryId}`;
      if (subcategoryId) url += `&subcategory_id=${subcategoryId}`;
      
      const data = await apiCall(url);
      setAttributes(data.attributes || []);
      setFilterSets(data.filter_sets || []);
    } catch {
      setAttributes([]);
    } finally {
      setLoading(false);
    }
  }, [categoryId, subcategoryId, apiCall]);

  useEffect(() => { fetchAttributes(); }, [fetchAttributes]);

  // Keyword auto-match when title changes
  useEffect(() => {
    if (!productTitle || attributes.length === 0) return;
    
    const timer = setTimeout(async () => {
      try {
        const data = await apiCall('/api/filter-sets/keyword-match', {
          method: 'POST',
          body: JSON.stringify({
            title: productTitle,
            attributes: attributes.map(a => ({ key: a.key, allowed_values: a.allowed_values }))
          })
        });
        setSuggestions(data.suggestions || {});
      } catch {
        // Ignore
      }
    }, 800);
    
    return () => clearTimeout(timer);
  }, [productTitle, attributes, apiCall]);

  // Apply a single suggestion
  const applySuggestion = (key, value) => {
    onChange({ ...values, [key]: value });
    setSuggestions(s => { const n = { ...s }; delete n[key]; return n; });
  };

  // Apply all suggestions
  const applyAllSuggestions = () => {
    const merged = { ...values, ...suggestions };
    onChange(merged);
    setSuggestions({});
  };

  const handleChange = (key, value) => {
    onChange({ ...values, [key]: value });
  };

  const handleMultiToggle = (key, val) => {
    const current = Array.isArray(values[key]) ? values[key] : [];
    const next = current.includes(val)
      ? current.filter(v => v !== val)
      : [...current, val];
    onChange({ ...values, [key]: next });
  };

  // iter-294 Bug-2 fix — when the parent has a stored category NAME
  // but the name→ID resolution failed (taxonomy rename / whitespace /
  // casing drift), render a visible diagnostic instead of returning
  // null. Users previously had NO indication the section existed and
  // filed "Filters option missing" bugs (Hare Krishna Enterprises).
  const _stuckOnDrift = !categoryId && !loading && !!categoryNameHint;
  if (!categoryId && !loading && !_stuckOnDrift) return null;
  
  const hasContent = attributes.length > 0 || loading;
  const pendingSuggestions = Object.keys(suggestions).filter(k => !values[k] || values[k] === '');
  const requiredMissing = attributes.filter(a => a.required && !values[a.key]);

  const renderAttributeField = (attr) => {
    const key = attr.key;
    const currentVal = values[key];
    const hasSuggestion = suggestions[key] && !currentVal;
    const isMissing = attr.required && !currentVal;

    return (
      <div key={key} className="space-y-1" data-testid={`filter-attr-${key}`}>
        <Label className="text-sm flex items-center gap-1.5">
          {attr.label}
          {attr.required && <Lock size={11} className="text-orange-500" />}
          {hasSuggestion && (
            <button
              onClick={() => applySuggestion(key, suggestions[key])}
              className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full hover:bg-amber-200 transition-colors ml-auto"
            >
              <Sparkles size={10} className="inline mr-0.5" /> {suggestions[key]}
            </button>
          )}
        </Label>

        {attr.input_type === 'select' && (
          <Select value={currentVal || ''} onValueChange={(v) => handleChange(key, v)}>
            <SelectTrigger className={`h-9 ${isMissing ? 'border-orange-300' : ''}`}>
              <SelectValue placeholder={`Select ${attr.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {(attr.allowed_values || []).map(v => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {attr.input_type === 'multi_select' && (
          <div className="flex flex-wrap gap-1.5">
            {(attr.allowed_values || []).map(v => {
              const selected = Array.isArray(currentVal) && currentVal.includes(v);
              return (
                <button
                  key={v}
                  onClick={() => handleMultiToggle(key, v)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    selected
                      ? 'bg-violet-600 text-white border-violet-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-violet-300'
                  }`}
                >
                  {selected && <CheckCircle size={10} className="inline mr-1" />}
                  {v}
                </button>
              );
            })}
          </div>
        )}

        {attr.input_type === 'text' && (
          <Input
            value={currentVal || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            placeholder={`Enter ${attr.label.toLowerCase()}`}
            className={`h-9 ${isMissing ? 'border-orange-300' : ''}`}
          />
        )}
      </div>
    );
  };

  // When embedded inside another card (e.g. Product Information), render without Card wrapper
  if (embedded) {
    return (
      <div className="space-y-3 pt-2 border-t border-violet-200/50" data-testid="filter-attributes-card">
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-violet-600" />
          <span className="text-sm font-semibold text-gray-800">Filter Attributes</span>
          <Badge variant="secondary" className="text-[10px] font-normal">Shopify Filters</Badge>
        </div>
        {_stuckOnDrift && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 flex items-start gap-2" data-testid="filter-attr-drift-warning">
            <AlertCircle size={14} className="text-orange-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-orange-900 font-medium">
                Category &quot;{categoryNameHint}&quot; no longer maps to the current taxonomy.
              </p>
              <p className="text-[11px] text-orange-700 mt-0.5">
                Filter Attributes are hidden until you reselect the category from the dropdown above. This usually happens when a category was renamed after this listing was created.
              </p>
            </div>
          </div>
        )}
        {!hasContent && !_stuckOnDrift && (
          <p className="text-xs text-gray-400">No filter attributes configured for this category. Set up in CMS &gt; Filter Sets.</p>
        )}
        {hasContent && (
          <>
            {loading ? (
              <div className="text-xs text-gray-400 animate-pulse">Loading...</div>
            ) : (
              <>
                {pendingSuggestions.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2" data-testid="keyword-suggestions">
                    <Sparkles size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-900">
                        Auto-detected: {pendingSuggestions.map(k => {
                          const attr = attributes.find(a => a.key === k);
                          return `${attr?.label || k} = "${suggestions[k]}"`;
                        }).join(', ')}
                      </p>
                      <Button variant="link" size="sm" onClick={applyAllSuggestions} className="text-amber-700 p-0 h-auto text-[10px]">
                        Apply all
                      </Button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {attributes.map(attr => renderAttributeField(attr))}
                </div>
                {requiredMissing.length > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px] text-orange-600">
                    <AlertCircle size={12} />
                    <span>{requiredMissing.length} required: {requiredMissing.map(a => a.label).join(', ')}</span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <Card className={`border-2 ${hasContent ? 'border-violet-200 bg-violet-50/30' : 'border-gray-200 bg-gray-50/30'}`} data-testid="filter-attributes-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Filter size={18} className="text-violet-600" />
          Filter Attributes
          <Badge variant="secondary" className="text-[10px] font-normal">Shopify Discovery</Badge>
        </CardTitle>
        <CardDescription className="text-xs">
          {attributes.length > 0 
            ? 'These attributes power Shopify storefront filters. Required fields must be filled before submission.'
            : 'No filter attributes are configured for this category yet. Admins can set them up in CMS > Filter Sets.'}
          {filterSets.length > 0 && (
            <span className="block mt-0.5 text-violet-600">
              Sets: {filterSets.map(s => s.name).join(', ')}
            </span>
          )}
        </CardDescription>
      </CardHeader>

      {_stuckOnDrift && (
      <CardContent className="pb-4">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2" data-testid="filter-attr-drift-warning">
          <AlertCircle size={16} className="text-orange-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-orange-900 font-medium">
              Category &quot;{categoryNameHint}&quot; no longer maps to the current taxonomy.
            </p>
            <p className="text-xs text-orange-700 mt-1">
              Filter Attributes are hidden until you reselect the category from the dropdown above. This usually happens when a category was renamed after this listing was created.
            </p>
          </div>
        </div>
      </CardContent>
      )}

      {hasContent && (
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-gray-500 animate-pulse py-4 text-center">Loading filter attributes...</div>
        ) : (
          <>
            {/* Auto-fill suggestions banner */}
            {pendingSuggestions.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="keyword-suggestions">
                <Sparkles size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    Auto-detected from title: {pendingSuggestions.map(k => {
                      const attr = attributes.find(a => a.key === k);
                      return `${attr?.label || k} = "${suggestions[k]}"`;
                    }).join(', ')}
                  </p>
                  <Button variant="link" size="sm" onClick={applyAllSuggestions} className="text-amber-700 p-0 h-auto text-xs mt-0.5">
                    Apply all suggestions
                  </Button>
                </div>
              </div>
            )}

            {/* Attribute inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {attributes.map(attr => renderAttributeField(attr))}
            </div>

            {/* Validation summary */}
            {requiredMissing.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                <AlertCircle size={14} />
                <span>{requiredMissing.length} required filter attribute{requiredMissing.length > 1 ? 's' : ''} still empty: {requiredMissing.map(a => a.label).join(', ')}</span>
              </div>
            )}
          </>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default FilterAttributesCard;
